/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ADMIN_ORDER_DETAILS,
  ArrowRed,
  DeliveryCreationType,
  Disclaimer,
  Div,
  H3,
  H5,
  Icon,
  InfoIcon,
  ModalFullScreen,
  P,
  PropsModal,
  Separator,
  SvgInlineWrapper,
  centsToDollars,
  formatter,
  lightTheme,
  renderProviderName,
} from '@vgrubs/components';
import { DateTime } from 'luxon';
import { Tooltip } from 'react-tooltip';
import {
  TextWithTooltipIcon,
  TitleWithTooltipIcon,
} from '../TransactonsStyled';
import {
  NewTransactionResponse,
  vDriveTransactionEntity,
} from 'components/src/types/api/wallet.types';

import { openInNewTab } from '../helpers';

const DEBT_REPAYMENT_TOOLTIP = 'debt-repayment-info';

/** The backend's own text, one line per `\n`; shown as is, never parsed. */
const DebtRepaymentTooltip = ({
  message,
  maxWidth,
}: {
  message: string;
  maxWidth: string;
}) => (
  <Tooltip
    clickable
    id={DEBT_REPAYMENT_TOOLTIP}
    style={{
      maxWidth,
      background: lightTheme.white,
      color: lightTheme.darkGray,
      padding: 16,
      borderRadius: 20,
      boxShadow: '0px 10px 15px rgba(0, 0, 0, 0.05)',
      zIndex: '1',
    }}
    border="1px solid #e3e3e3"
  >
    <span style={{ whiteSpace: 'pre-line' }}>{message}</span>
  </Tooltip>
);

export const TransactionDetailModal = ({
  modalIsOpen,
  closeModal,
}: PropsModal & {
  modalIsOpen: NewTransactionResponse<vDriveTransactionEntity> | null;
}) => {
  const transaction = modalIsOpen?.object;
  const isCancelledOrder = transaction?.paidCancellationFee;
  const isManual = transaction?.creationType === DeliveryCreationType.manual;
  const debtRepayment = transaction?.debtRepayment ?? 0;
  const debtRepaymentMessage = transaction?.debtRepaymentInfo?.message;

  if (!transaction) return null;

  return (
    <>
      <ModalFullScreen isOpen={!!modalIsOpen} closeModal={closeModal}>
        <H3>Transaction Details</H3>
        <P $m="0">Date & Time</P>
        <H5 $m="0">
          {DateTime.fromISO(transaction?.createdAt || '').toFormat(
            'MMM dd, yyyy - hh:mm a'
          )}
        </H5>
        <Separator $m="16px 0" />
        {!isManual && (
          <>
            <Div $flex $alignCenter $between>
              <Div>
                <P $m="0">Order ID</P>
                <H5 $m="0">#{transaction?.orderId}</H5>
              </Div>
              <Div
                $pointer
                onClick={() =>
                  openInNewTab(
                    `${ADMIN_ORDER_DETAILS.replace(
                      ':id',
                      transaction?.orderId?.toString() || ''
                    )}`
                  )
                }
              >
                <Icon>
                  <ArrowRed />
                </Icon>
              </Div>
            </Div>
            <Separator $m="16px 0" />
          </>
        )}

        <P $m="0">Description</P>
        <H5 $m="0">
          {isCancelledOrder
            ? 'Stop vDriver Assignment'
            : `vDrive Transaction ${isManual ? '(manual request)' : ''}`}
        </H5>
        <Separator $m="16px 0" />

        {isCancelledOrder && (
          <Div $box $p="16px">
            <TextWithTooltipIcon
              text="Cancellation Fee"
              tooltip={`Reason: ${transaction.cancellationReason}`}
            />
            <H5 $m="0" $color={lightTheme.primary}>
              {formatter.format(
                centsToDollars(transaction?.paidCancellationFee || 0)
              )}
            </H5>
          </Div>
        )}

        {!isCancelledOrder && (
          <>
            <Div $flex $column $gap="40px" $gapSM="0">
              {!isManual && (
                <Div $width="100%" $widthSM="100%">
                  <TitleWithTooltipIcon
                    text={`Deposit from ${renderProviderName(
                      transaction?.provider
                    )}`}
                    tooltip={
                      'A report of how much total additional revenue was deposited into your bank from delivery providers. Without vDrive, this additional revenue would not have been deposited.'
                    }
                  />

                  <Div $flex $columnSM $gap="40px" $gapSM="0">
                    <Div $width="50%" $widthSM="100%">
                      <TextWithTooltipIcon
                        text="Delivery Fee"
                        tooltip={`The delivery fee the diner paid, which is additional income deposited into the restaurant owner's account with vDrive.`}
                      />
                      <H5 $m="0" $color={lightTheme.green}>
                        {formatter.format(
                          centsToDollars(transaction?.deliveryFee || 0)
                        )}
                      </H5>
                      <Separator $m="16px 0" />
                      <TextWithTooltipIcon
                        text="Delivery Tip"
                        tooltip={`The delivery tip to be forwarded to the delivery driver.`}
                      />
                      <H5 $m="0" $color={lightTheme.green}>
                        {formatter.format(
                          centsToDollars(transaction?.deliveryTip || 0)
                        )}
                      </H5>
                      <Separator $m="16px 0" />
                    </Div>
                    <Div $width="50%" $widthSM="100%">
                      <TextWithTooltipIcon
                        text="Est. Savings From Commission Reduction"
                        tooltip={`The dollar amount saved from reducing your delivery app commissions by opting into vDrive.`}
                      />

                      {transaction?.esfcr === null ? (
                        <H5 $m="0">CONTRACT PENDING</H5>
                      ) : (
                        <H5
                          $m="0"
                          $color={
                            transaction?.esfcr >= 0
                              ? lightTheme.green
                              : lightTheme.primary
                          }
                        >
                          {formatter.format(
                            centsToDollars(transaction?.esfcr || 0)
                          )}
                        </H5>
                      )}

                      <Separator $m="16px 0" />

                      <P $m="0">Total Amount</P>
                      <H5 $m="0" $color={lightTheme.green}>
                        {formatter.format(
                          centsToDollars(transaction?.depositTotal || 0)
                        )}
                      </H5>
                      <Separator $m="16px 0" />
                    </Div>
                  </Div>
                </Div>
              )}
              <Div $width="100%" $widthSM="100%">
                <H3>Deductions from vDrive</H3>
                <P $m="0">Delivery Network Fee</P>
                <H5 $m="0" $color={lightTheme.primary}>
                  {formatter.format(
                    centsToDollars(transaction?.networkFee || 0)
                  )}
                </H5>

                <Separator $m="16px 0" />

                {transaction?.fundedTip === null ? (
                  <>
                    <P $m="0">Delivery Tip</P>
                    <H5 $m="0" $color={lightTheme.primary}>
                      {formatter.format(
                        centsToDollars(transaction?.deliveryTip || 0)
                      )}
                    </H5>
                    <Separator $m="16px 0" />
                  </>
                ) : (
                  <Div $flex $columnSM $gap="40px" $gapSM="0">
                    <Div $width="50%" $widthSM="100%">
                      <P $m="0">Delivery Tip</P>
                      <H5 $m="0" $color={lightTheme.primary}>
                        {formatter.format(
                          centsToDollars(transaction?.deliveryTip || 0)
                        )}
                      </H5>
                      <Separator $m="16px 0" />
                    </Div>
                    <Div $width="50%" $widthSM="100%">
                      <TextWithTooltipIcon
                        text="Funded Tip"
                        tooltip={`Tip funded by restaurant owner to prevent delivery driver tips being $0 or below a certain amount. This increases likelihood of successful delivery, and decreases assignment time drastically.`}
                      />
                      <H5 $m="0" $color={lightTheme.primary}>
                        {formatter.format(
                          centsToDollars(transaction?.fundedTip || 0)
                        )}
                      </H5>
                      <Separator $m="16px 0" />
                    </Div>
                  </Div>
                )}

                <Div $flex $columnSM $gap="40px" $gapSM="0">
                  <Div $width="50%" $widthSM="100%">
                    <P $m="0">vGrubs Fee</P>
                    <H5 $m="0" $color={lightTheme.primary}>
                      {formatter.format(
                        centsToDollars(transaction?.vGrubsFee || 0)
                      )}
                    </H5>
                    <Separator $m="16px 0" />
                  </Div>
                  <Div $width="50%" $widthSM="100%">
                    <P $m="0">Total Amount</P>
                    <H5 $m="0" $color={lightTheme.primary}>
                      {formatter.format(
                        centsToDollars(transaction?.totalAmount || 0)
                      )}
                    </H5>
                    <Separator $m="16px 0" />
                  </Div>
                </Div>
              </Div>
            </Div>
            {!isManual && (
              <Div
                $flex
                $between
                $width="50%"
                $widthSM="100%"
                $m="16px 0"
                $mSM="8px 0"
              >
                <div>
                  <H3 $mb="4">Est. vDrive Savings:</H3>
                  <Disclaimer $mb="0">(Deposit - Deduction)</Disclaimer>
                </div>
                <H5
                  $m="0"
                  $color={
                    (transaction?.depositTotal || 0) -
                      (transaction?.totalAmount || 0) >=
                    0
                      ? lightTheme.green
                      : lightTheme.primary
                  }
                >
                  {formatter.format(
                    centsToDollars(
                      (transaction?.depositTotal || 0) -
                        (transaction?.totalAmount || 0)
                    )
                  )}
                </H5>
              </Div>
            )}
            {/* Its own block under the savings: it is charged with the
                delivery but is part of neither the deduction nor the
                savings, so it must not read as one of their lines. */}
            {debtRepayment > 0 && (
              <>
                <Div $width="50%" $widthSM="100%">
                  <Separator $m="16px 0 0" />
                </Div>
                <Div
                  $flex
                  $between
                  $gap="16px"
                  $width="50%"
                  $widthSM="100%"
                  $m="16px 0"
                  $mSM="8px 0"
                >
                  <div>
                    <H3 $mb="4">
                      Previous Transaction Due Balance:
                      {debtRepaymentMessage && (
                        <>
                          {' '}
                          <a
                            data-tooltip-id={DEBT_REPAYMENT_TOOLTIP}
                            data-tooltip-place="top"
                            style={{ color: lightTheme.darkGray }}
                          >
                            <SvgInlineWrapper>
                              <InfoIcon />
                            </SvgInlineWrapper>
                          </a>
                        </>
                      )}
                    </H3>
                    <Disclaimer $mb="0">
                      (collected under your repayment plan, not part of savings)
                    </Disclaimer>
                  </div>
                  <H5 $m="0" $color={lightTheme.primary}>
                    -{formatter.format(centsToDollars(debtRepayment))}
                  </H5>
                </Div>
                {debtRepaymentMessage && (
                  <>
                    <Div $desktop>
                      <DebtRepaymentTooltip
                        message={debtRepaymentMessage}
                        maxWidth="500px"
                      />
                    </Div>
                    <Div $mobile>
                      <DebtRepaymentTooltip
                        message={debtRepaymentMessage}
                        maxWidth="95%"
                      />
                    </Div>
                  </>
                )}
              </>
            )}
          </>
        )}
      </ModalFullScreen>
      <Div $desktop>
        <Tooltip
          id="my-tooltip"
          style={{
            maxWidth: '500px',
            background: lightTheme.white,
            color: lightTheme.darkGray,
            padding: 16,
            borderRadius: 20,
            boxShadow: '0px 10px 15px rgba(0, 0, 0, 0.05)',
            zIndex: '1',
          }}
          border="1px solid #e3e3e3"
        />
      </Div>
      <Div $mobile>
        <Tooltip
          id="my-tooltip"
          style={{
            maxWidth: '95%',
            background: lightTheme.white,
            color: lightTheme.darkGray,
            padding: 16,
            borderRadius: 20,
            boxShadow: '0px 10px 15px rgba(0, 0, 0, 0.05)',
            zIndex: '1',
          }}
          border="1px solid #e3e3e3"
        />
      </Div>
    </>
  );
};
